import { Badge, Col, Image, Row, Table, Typography, Button, Dropdown, Checkbox } from "antd"
import { useState } from "react"

import { OrderLocation } from "../../../components/Orders/OrderLocation"
import moment from "moment"
import { returnProperMachineTag } from "../../../components/utils/ReturnProperMachineTag"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import UserActionDrawer from "../../../components/Manager/Order/Action/UserActionDrawer"
import "../../../styles/OrderTable.css"
import ColumnSearchFilter from "../../../components/Table/orderNumberFilter"
import { SearchOutlined } from "@ant-design/icons"
import { filterOrdersByRole } from "../../../utils/filterOrdersByRole"
import { useAuthContext } from "../../../hooks/useAuthContext"
import MissingPaperSummary from "./Helper/MissingPaperSummary"

const { Text } = Typography

export default function AllOrdersTable({ loading, data }) {
  const { admin, semiAdmin, devSpecialist } = useAuthContext()

  // Column visibility state - Manufacturer and Created hidden by default
  const [visibleColumns, setVisibleColumns] = useState({
    nr: true,
    location: true,
    client: true,
    machine: true,
    product: true,
    manufacturer: false,
    labelCount: true,
    technical: true,
    dueDate: true,
    created: false,
    action: true,
  })

  //loc filterr
  let locationFilter = data?.reduce((accumulator, item) => {
    const filterValue = item.orderStatus // Assuming orderStatus is the field you're filtering on

    // Only add new item if it does not exist yet (no parseInt conversion)
    if (!accumulator.some((filterItem) => filterItem.value === filterValue)) {
      accumulator.push({
        text: OrderLocation(filterValue), // Assuming OrderLocation is a function that returns the display text
        value: filterValue,
      })
    }

    return accumulator
  }, [])

  // Machine filter
  let machineFilters =
    data &&
    data.reduce((accumulator, item) => {
      // Assuming "machine" data is under item.selectedProduct.machine
      const machineName = item.selectedProduct.machine
      const existingItem = accumulator.find((filterItem) => filterItem.value === machineName)

      if (!existingItem) {
        accumulator.push({
          text: machineName, // Assuming you have a function to get a friendly name for each machine
          value: machineName,
        })
      }

      return accumulator
    }, [])

  //client filter
  let clientFIlters =
    data &&
    data.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  //client filter
  let productFilters =
    data &&
    data.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  const filteredData = filterOrdersByRole(data, { admin, semiAdmin, devSpecialist })

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "left",
      width: 250,
      sorter: (a, b) => a.justOrderNumber - b.justOrderNumber,
      render: (value, record) => OrderAndPrefix(record),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <ColumnSearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          dataIndex="orderNumber"
        />
      ),
      onFilter: (value, record) =>
        record.orderNumber.toString().trim().toLowerCase().includes(value.trim().toLowerCase()),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
    },
    {
      title: "Location",
      dataIndex: "orderStatus",
      align: "left",
      width: 100,
      render: (value) => OrderLocation(value),
      filters: locationFilter,
      onFilter: (value, record) => record.orderStatus === value,
      filterSearch: true,
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      width: 160,
      ellipsis: true,
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      width: 100,
      filters: machineFilters,
      onFilter: (value, record) => record.selectedProduct.machine === value,
      render: (value) => {
        return returnProperMachineTag(value)
      },
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      render: (value, record) => {
        // Safely access changableSpace using optional chaining and logical OR
        const changableSpace = record.selectedProduct?.advanced?.changableSpace || false
        if (changableSpace) {
          return (
            <Text>
              {value} - ({record.changableSpaceData})
            </Text>
          )
        } else {
          return <Text>{value}</Text>
        }
      },
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      width: 120,
      align: "center",
    },
    {
      title: "Amount",
      align: "center",
      width: 100,
      render: (record) => {
        if (record.selectedProduct.machine === "Tape") {
          return <Text>{record.rollCount}</Text>
        } else {
          return <Text>{record.labelCount}</Text>
        }
      },
    },
    {
      title: "File",
      dataIndex: "technicalPage",
      align: "center",
      width: 70,

      render: (value) => {
        return <Image src={value} width={25} height={25} />
      },
    },
    {
      title: "Due date",
      dataIndex: "orderDueDate",
      align: "center",
      width: 100,
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      render: (value) => {
        const dueDateMoment = moment.unix(value.seconds)
        const today = moment().startOf("day")
        const isDueOrPast = dueDateMoment.isSameOrBefore(today, "day")
        return (
          <Text type={isDueOrPast ? "danger" : undefined}>
            {dueDateMoment.format("DD.MM.YYYY")}
          </Text>
        )
      },
    },
    {
      title: "Created",
      dataIndex: "created",
      align: "center",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => <Text>{moment.unix(value.seconds).format("DD/MM/YYYY")}</Text>,
    },
    {
      title: "Edit",
      align: "center",
      width: 100,
      render: (value) => <UserActionDrawer data={value} />,
    },
  ]

  // Create columns map
  const allColumnsMap = {
    nr: defaultColumns[0],
    location: defaultColumns[1],
    client: defaultColumns[2],
    machine: defaultColumns[3],
    product: defaultColumns[4],
    manufacturer: defaultColumns[5],
    labelCount: defaultColumns[6],
    technical: defaultColumns[7],
    dueDate: defaultColumns[8],
    created: defaultColumns[9],
    action: defaultColumns[10],
  }

  // Filter columns based on visibility
  const visibleColumnsArray = Object.keys(allColumnsMap)
    .filter((key) => visibleColumns[key])
    .map((key) => allColumnsMap[key])

  // Column toggle menu items
  const columnMenuItems = Object.keys(visibleColumns).map((key) => ({
    key,
    label: (
      <Checkbox
        checked={visibleColumns[key]}
        onChange={() => setVisibleColumns({ ...visibleColumns, [key]: !visibleColumns[key] })}>
        {allColumnsMap[key]?.title || key}
      </Checkbox>
    ),
  }))

  return (
    <>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <Row justify="space-between" align="middle" style={{ marginBottom: "12px" }}>
            <Col>
              {admin || semiAdmin || devSpecialist ? (
                <MissingPaperSummary data={data} loading={loading} />
              ) : (
                ""
              )}
            </Col>
            <Col>
              <Dropdown menu={{ items: columnMenuItems }} placement="bottomRight">
                <Button type="text" style={{ padding: 0 }}>
                  Hide/Show Columns ▼
                </Button>
              </Dropdown>
            </Col>
          </Row>
          <Table
            style={{ marginTop: "0px" }}
            size="small"
            loading={loading}
            columns={visibleColumnsArray}
            bordered
            dataSource={filteredData}
            rowKey={(record) => record.id}
            pagination={{ defaultPageSize: "25", showSizeChanger: true }}
            rowClassName={(record) => (record.priorityOrder ? "red-background" : "")}
          />
        </Col>
      </Row>
    </>
  )
}
